import React, { useEffect, useState } from 'react';
import {
    Box,
    CircularProgress,
    Typography,
    Button
} from '@mui/material';
import { getQuestionnaire, saveQuestionnaire } from '../../services/questionnaireService';
import { getEventInfo } from '../../services/EventInformation';
import { EventQuestionnaire } from '../../data/questionnaire';
import QuestionnaireSection from './QuestionnaireSection';

interface Props {
    eventId: string;
    onBack?: () => void;
}

const QuestionnairePage: React.FC<Props> = ({ eventId, onBack }) => {
    const [loading, setLoading] = useState(true);
    const [questionnaire, setQuestionnaire] = useState<EventQuestionnaire | null>(null);
    const [isEditable, setIsEditable] = useState(false);
    const [eventName, setEventName] = useState('');
    const [openAddQuestionModal, setOpenAddQuestionModal] = useState(false);

    useEffect(() => {
        if (!eventId) return;

        setLoading(true);
        setQuestionnaire(null);
        setIsEditable(false);

        const fetchData = async () => {
            try {
                const [questionnaireData, eventList] = await Promise.all([
                    getQuestionnaire(eventId),
                    getEventInfo(),
                ]);

                const matchingEvent = eventList.find(event => event.Id === eventId);
                if (matchingEvent) {
                    setIsEditable((matchingEvent.RegisteredUserCount ?? 0) === 0);
                    setEventName(matchingEvent.Name || '');
                }

                setQuestionnaire(questionnaireData);
            } catch (error) {
                console.error('Failed to load questionnaire or event info:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [eventId]);

    const handleCreateQuestionnaire = async () => {
        try {
            const newQuestionnaire: EventQuestionnaire = {
                eventId,
                questions: [],
            };

            const saved = await saveQuestionnaire(eventId, newQuestionnaire);
            setQuestionnaire(saved);
            setOpenAddQuestionModal(true); // Automatically open modal
        } catch (error) {
            console.error('Failed to create questionnaire:', error);
            alert('Failed to create questionnaire. Please try again.');
        }
    };

    if (loading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="50vh"
            >
                <CircularProgress color="info" size={48} />
                <Typography mt={2} color="textSecondary">
                    Loading questionnaire…
                </Typography>
            </Box>
        );
    }

    return (
        <Box p={4} key={eventId}>
            <Typography variant="h4" gutterBottom>
                {eventName ? `${eventName} Questionnaire` : 'Questionnaire'}
            </Typography>

            {questionnaire ? (
                <QuestionnaireSection
                    questionnaire={questionnaire}
                    isEditable={isEditable}
                    setQuestionnaire={setQuestionnaire}
                    eventId={eventId}
                    onBack={onBack}
                    forceOpenModal={openAddQuestionModal}
                    onModalClose={() => setOpenAddQuestionModal(false)}
                />
            ) : isEditable ? (
                <Box mt={2}>
                    <Typography>No questionnaire found. You can create one below.</Typography>
                    <Button
                        variant="contained"
                        sx={{ mt: 2, backgroundColor: '#1976d2 !important', color: '#fff !important' }}
                        onClick={handleCreateQuestionnaire}
                    >
                        Create Questionnaire
                    </Button>
                </Box>
            ) : (
                <Typography>No questionnaire available for this event.</Typography>
            )}
        </Box>
    );
};

export default QuestionnairePage;